import React, { FocusEvent, forwardRef, ReactElement, useCallback, useImperativeHandle, useRef } from 'react'
import { useKey } from 'react-use'

import { Color } from '../../enums/color'
import { Icon } from '../Icon'
import { Input, InputProps, InputRef } from '../Input'
import * as Styled from './styles'

export type SearchInputSize = 'sm' | 'md'
export type SearchInputProps = Omit<InputProps, 'isRounded' | 'prefix' | 'size'> & {
  size?: SearchInputSize
  fullWidth?: boolean
}

export const SearchInput = forwardRef<InputRef, SearchInputProps>(
  (
    { fullWidth = false, onBlur, onChange, onChangeDebounced, placeholder, size = 'sm', ...rest }: SearchInputProps,
    forwardedRef,
  ): ReactElement => {
    const searchInputRef = useRef<InputRef>(null)

    useImperativeHandle(forwardedRef, () => searchInputRef?.current as InputRef)

    const handleInputBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        if (!event.target.value) {
          onBlur?.(event)
        }
      },
      [onBlur],
    )

    const handleEscapeKey = useCallback(
      (event: KeyboardEvent) => {
        const { target } = event
        const inputElement = searchInputRef.current?.input

        if (target === inputElement) {
          inputElement.blur()
        }
      },
      [searchInputRef],
    )

    useKey('Escape', handleEscapeKey)

    return (
      <Styled.SearchInputWrapper size={size} fullWidth={fullWidth}>
        <Styled.InputWrapper fullWidth={fullWidth}>
          <Input
            {...rest}
            allowClear
            onBlur={handleInputBlur}
            onChange={onChange}
            onChangeDebounced={onChangeDebounced}
            placeholder={placeholder}
            prefix={<Icon icon="search" color={Color.Grey800} />}
            ref={searchInputRef}
            isRounded={true}
            size={size}
          />
        </Styled.InputWrapper>
      </Styled.SearchInputWrapper>
    )
  },
)
